import React, { useState, useRef } from 'react';
import { Box, Tabs, Tab, Button } from '@mui/material';
import { Formik, Form } from 'formik';
import VitalsForm from './VitalsForm';
import HistoryForm from './HistoryForm';
import CurrentMedicationForm from './CurrentMedicationForm';
import SignsSymptomsForm from './SignsSymptomsForm';
import PhysicalExaminationForm from './PhysicalExaminationForm';
import { initialValues } from './initialValues';
import { validationSchema } from './validationSchema';
import * as Yup from 'yup';
import { ncdAPI } from "../../../../services/ncd.api";
import { toast } from "react-toastify";
import './InitialAssessmentForm.css';

const tabs = [
  { label: 'Vitals', value: 0 },
  { label: 'History', value: 1 },
  { label: 'Current Medication', value: 2 },
  { label: 'Signs & Symptoms', value: 3 },
  { label: 'Physical Examination', value: 4 }
];

const InitialAssessmentForm = ({ setShowInitialAssessment, individualId, gender }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isHistoryEditing, setIsHistoryEditing] = useState(false);
  const [isSymptomsEditing, setIsSymptomsEditing] = useState(false);
  const physicalExaminationFormRef = useRef();

  const patientGender = gender || (() => {
    try {
      const stored = localStorage.getItem("beneficiaryDetails");
      if (stored) {
        return JSON.parse(stored).gender;
      }
    } catch (e) {
      console.error("Error reading beneficiaryDetails from localStorage", e);
    }
    return "";
  })();

  const handleTabChange = (event, newValue) => {
    setDirection(newValue > activeTab ? 1 : -1);
    setActiveTab(newValue);
  };

  const handleClose = () => {
    setShowInitialAssessment(false);
  };

  const handleSkip = () => {
    if (activeTab < tabs.length - 1) {
      setDirection(1);
      setActiveTab(activeTab + 1);
    } else {
      setDirection(-1);
      setActiveTab(0);
    }
  };

  // Submit history data to API
  const submitHistoryToAPI = async (values) => {
    const formatDate = (dateString) => {
      const date = dateString ? new Date(dateString) : new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const assesDate = formatDate(values.historyDate);

    // Convert yes/no to true/false
    const convertYesNo = (value) => {
      if (value === 'yes') return true;
      if (value === 'no') return false;
      return null;
    };

    const payload = {
      assesDate: assesDate,
      diabts: values.history?.diabetes || false,
      hypertsn: values.history?.hypertension || false,
      copd: values.history?.copd || false,
      ckd: values.history?.ckd || false,
      LiverDiseases: values.history?.liverFailure || false,
      pvd: values.history?.pvd || false,
      heartAtck: values.history?.heartAttack || false,
      strk: values.history?.stroke || false,
      heartDisease: values.history?.heartDisease || false,
      pregnt: convertYesNo(values.history?.pregnant),
      diabetesReportVerified: values.history?.diabetesVerified || false,
      copdReportVerified: values.history?.copdVerified || false,
      ckdReportVerified: values.history?.ckdVerified || false,
      liverDiseasesReportVerified: values.history?.liverFailureVerified || false,
      pvdReportVerified: values.history?.pvdVerified || false,
      heartAttackReportVerified: values.history?.heartAttackVerified || false,
      strkReportVerified: values.history?.strokeVerified || false,
      heartDiseaseReportVerified: values.history?.heartDiseaseVerified || false,
      fmlyHstryPreMI: convertYesNo(values.history?.familyHistory?.prematureMI),
      fmlyHstryStrk: convertYesNo(values.history?.familyHistory?.stroke),
      fmlyHstryDiabts: convertYesNo(values.history?.familyHistory?.diabetes),
      smkng: convertYesNo(values.history?.tobaccoConsumption?.smokesTobacco),
      smkls: convertYesNo(values.history?.tobaccoConsumption?.smokelessTobacco),
      alcohol: values.history?.alcoholConsumption || ''
    };

    await ncdAPI.submitInitialAssessmentHistory(individualId, payload);
  };

  // Submit medications data to API
  const submitMedicationsToAPI = async (values) => {
    const formatDate = (dateString) => {
      const date = dateString ? new Date(dateString) : new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const assesDate = formatDate(values.currentMedicationDate);

    // Filter medications with medicineSourceType: 1
    const filteredMedications = (values.medications || []).filter(med => {
      return med.medicine && med.condition && med.frequency;
    }).map(med => ({
      date: assesDate,
      conditn: med.condition,
      conditnOthr: med.condition === 'Other' ? (med.conditionOther || '') : '',
      name: med.medicine,
      freq: med.frequency,
      duratn: med.duration ? String(med.duration) : '1',
      quan: med.quantity ? (parseInt(med.quantity) || 1) : 1,
      spInst: med.instruction || '',
      medicineSourceType: 1
    }));

    const payload = {
      assesDate: assesDate,
      medicines: filteredMedications
    };

    await ncdAPI.submitInitialAssessmentMedications(individualId, payload);
  };

  // Submit symptoms data to API
  const submitSymptomsToAPI = async (values) => {
    const formatDate = (dateString) => {
      const date = dateString ? new Date(dateString) : new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const assesDate = formatDate(values.symptomsDate);

    // Helper to convert symptoms based on "none" checkbox
    const convertSymptoms = (symptomsObj, isNone) => {
      const result = {};
      Object.keys(symptomsObj).forEach(key => {
        if (key !== 'other' && key !== 'othr') {
          result[key] = isNone ? null : (symptomsObj[key] || false);
        } else {
          result[key] = symptomsObj[key] || '';
        }
      });
      return result;
    };

    const payload = {
      assesDate: assesDate,
      general: values.symptoms?.general || {},
      oral: convertSymptoms(values.symptoms?.oral || {}, values.symptoms?.oralNone),
      breast: convertSymptoms(values.symptoms?.breast || {}, values.symptoms?.breastNone),
      cervical: convertSymptoms(values.symptoms?.cervical || {}, values.symptoms?.cervicalNone)
    };

    await ncdAPI.submitInitialAssessmentSymtoms(individualId, payload);
  };

  // Submit vitals data to API
  const submitVitalsToAPI = async (values) => {
    const formatDate = (dateString) => {
      const date = dateString ? new Date(dateString) : new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const assesDate = formatDate(values.date);

    const payload = {
      vitalsDate: new Date().toISOString(),
      bpVitals: {
        assesDate: assesDate,
        sys: values.vitals?.systolicBP || '',
        diast: values.vitals?.diastolicBP || '',
        heartRate: values.vitals?.heartRate || ''
      },
      sugarVitals: {
        assesDate: assesDate,
        rndBldGlc: values.vitals?.randomBloodSugar || '',
        fastngBldGlc: values.vitals?.fastingBloodSugar || '',
        postPradBldGlc: values.vitals?.postPrandialBloodSugar || '',
        hba1c: values.vitals?.hba1c || '',
        mthdOfBldTstPpbgCapillaryVenous: values.vitals?.postPrandialBloodSugarType || '',
        mthdOfBldTstRbsCapillaryVenous: values.vitals?.randomBloodSugarType || ''
      },
      measurementVitals: {
        assesDate: assesDate,
        height: values.vitals?.height || '',
        weight: values.vitals?.weight || '',
        bmi: values.vitals?.bmi ? parseFloat(values.vitals.bmi) : '',
        wstCirmfnc: values.vitals?.waistCircumference || ''
      }
    };

    await ncdAPI.submitInitialAssessmentVitals(individualId, payload);
  };

  const handleSaveAndContinue = async (values, formikHelpers) => {
    try {
      switch (activeTab) {
        case 0:
          await submitVitalsToAPI(values);
          // Clear vitals form fields after successful submission
          formikHelpers.setFieldValue('vitals', {
            systolicBP: '',
            diastolicBP: '',
            heartRate: '',
            fastingBloodSugar: '',
            postPrandialBloodSugar: '',
            randomBloodSugar: '',
            postPrandialBloodSugarType: '',
            randomBloodSugarType: '',
            hba1c: '',
            height: '',
            weight: '',
            bmi: '',
            waistCircumference: ''
          });
          // Reset date to today
          formikHelpers.setFieldValue('date', new Date().toISOString().split('T')[0]);
          toast.success('Vitals saved successfully!');
          break;
        case 1:
          await submitHistoryToAPI(values);
          // Refresh history data
          if (window.historyFormRef?.fetchHistory) {
            await window.historyFormRef.fetchHistory();
          }
          // Clear history form after successful submission
          formikHelpers.setFieldValue('history', {
            diabetes: false,
            hypertension: false,
            copd: false,
            ckd: false,
            liverFailure: false,
            pvd: false,
            heartAttack: false,
            stroke: false,
            heartDisease: false,
            diabetesVerified: false,
            hypertensionVerified: false,
            copdVerified: false,
            ckdVerified: false,
            liverFailureVerified: false,
            pvdVerified: false,
            heartAttackVerified: false,
            strokeVerified: false,
            heartDiseaseVerified: false,
            pregnant: '',
            familyHistory: {
              prematureMI: '',
              stroke: '',
              diabetes: ''
            },
            tobaccoConsumption: {
              smokesTobacco: '',
              smokelessTobacco: ''
            },
            alcoholConsumption: ''
          });
          formikHelpers.setFieldValue('historyDate', new Date().toISOString().split('T')[0]);
          toast.success('History saved successfully!');
          break;
        case 2:
          await submitMedicationsToAPI(values);
          // Clear medications form after successful submission
          formikHelpers.setFieldValue('medications', []);
          formikHelpers.setFieldValue('currentMedicationDate', new Date().toISOString().split('T')[0]);
          toast.success('Medication saved successfully!');
          break;
        case 3:
          await submitSymptomsToAPI(values);
          // Refresh symptoms data
          if (window.symptomsFormRef?.fetchSymptoms) {
            await window.symptomsFormRef.fetchSymptoms();
          }
          // Clear symptoms form after successful submission
          formikHelpers.setFieldValue('symptoms', {
            general: {
              excsThirst: null,
              excsUrinatn: null,
              excsHunger: null,
              recrntSkin: null,
              woundHeal: null,
              dietChng: null,
              visualDistrbnc: null,
              breathlsns: null,
              angina: null,
              claudicatn: null,
              limpness: null
            },
            oral: {
              ulcer: null,
              cavityPatch: null,
              diffOpngMouth: null,
              spicyFood: null,
              voiceChange: null,
              other: ''
            },
            breast: {
              lump: null,
              dischrg: null,
              retrctn: null,
              othr: ''
            },
            cervical: {
              bldDrngPrd: null,
              bldBtwnPrd: null,
              postMeno: null,
              coitalBld: null,
              foulSmell: null,
              othr: ''
            },
            oralNone: false,
            breastNone: false,
            cervicalNone: false
          });
          formikHelpers.setFieldValue('symptomsDate', new Date().toISOString().split('T')[0]);
          toast.success('Signs & Symptoms saved successfully!');
          break;
        case 4:
          if (physicalExaminationFormRef.current) {
            await physicalExaminationFormRef.current.submit();
          }
          toast.success('Physical Examination saved successfully!');
          break;
      }

      if (activeTab < tabs.length - 1) {
        setDirection(1);
        setActiveTab(activeTab + 1);
      } else {
        toast.success('Initial Assessment completed!');
        handleClose();
      }
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save data. Please try again.');
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const renderTabContent = (values, errors, touched) => {
    switch (activeTab) {
      case 0:
        return <VitalsForm individualId={individualId} gender={patientGender} />;
      case 1:
        return <HistoryForm setIsEditing={setIsHistoryEditing} individualId={individualId} />;
      case 2:
        return <CurrentMedicationForm individualId={individualId} />;
      case 3:
        return <SignsSymptomsForm setIsEditing={setIsSymptomsEditing} individualId={individualId} />;
      case 4:
        return <PhysicalExaminationForm ref={physicalExaminationFormRef} individualId={individualId} gender={patientGender} />;
      default:
        return null;
    }
  };

  return (
    <Box className="initial-assessment-container">
      <h3 className='followup-container-title'>Initial Assessment</h3>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        className="assessment-tabs"
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#666",
            "&.Mui-selected": {
              color: "#1976d2",
              fontWeight: 600,
            },
          }} />
        ))}
      </Tabs>

      <Button
        onClick={handleClose}
        sx={{
          border: "2px solid #A9A9A9",
          width: "30px",
          height: "30px",
          borderRadius: "100%",
          cursor: "pointer",
          position: "absolute",
          right: "-8px",
          top: "-10px",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "auto",
          p: 0,
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
        }}
      >
        <img src={"/images/closing.svg"} alt="Close" style={{ width: "16px" }} />
      </Button>

      <Formik
        initialValues={initialValues}
        validationSchema={activeTab === 0 ? validationSchema : Yup.object({ historyDate: Yup.date().nullable() })}
        onSubmit={handleSaveAndContinue}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ values, errors, touched, isSubmitting, handleSubmit }) => {
          return (
            <Form>
              <Box className="tab-content-wrapper">
                <div
                  key={activeTab}
                  className={`tab-content slide-${direction > 0 ? 'left' : 'right'}`}
                >
                  {renderTabContent(values, errors, touched)}
                </div>
              </Box>

              <Box className="form-actions">
                <Button onClick={handleSkip} variant="outlined" disabled={isSubmitting}>
                  SKIP
                </Button>
                <Box className="navigation-buttons">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || (activeTab === 1 && !isHistoryEditing) || (activeTab === 3 && !isSymptomsEditing)}
                  >
                    {isSubmitting ? 'SAVING...' : 'SAVE & CONTINUE'}
                  </Button>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default InitialAssessmentForm;
